/* eslint-disable default-case */
import React, { useReducer, useCallback } from 'react';
import { createContextualCan, useAbility } from '@casl/react';
import { useChannel, useEvent } from '@harelpls/use-pusher';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useHistory, useParams, useLocation, Redirect } from 'react-router-dom';
import { useQuery } from 'react-query';
import { toast } from 'components/Shared/Toast';
import produce from 'immer';
import { Button } from 'reactstrap';

import { fetchMembers } from 'api/actions/collaboration';
import { fetchProfile } from 'api/actions/profile';
import { defineAbilityFor } from 'casl';
import { fetcher } from 'api/client';
import { Growl } from 'components/Shared/Growl';
import { TratoLoader } from 'components/Shared/TratoLoader';
import { menus } from 'components/Main';

const ProfileStateContext = React.createContext();
const ProfileDispatchContext = React.createContext();
const ProfileAbilityContext = React.createContext();

const ProfileCan = createContextualCan(ProfileAbilityContext.Consumer);

export const SET_PROFILE = 'SET_PROFILE';
export const SET_STATUS = 'SET_STATUS';
export const SET_GROUPS = 'SET_GROUPS'

const initialState = {
  profile: {},
  roles: [],
  groups: []
}

const reducer = produce((draft, action) => {
  switch (action.type) {
    case SET_PROFILE:
      const roles = action.profile.roles ?? [];
      const role = action.profile.role ?? [];
      const tenantPrivileges = action.profile.tenant?.privileges ?? []
      draft.profile = action.profile;
      draft.roles = [...roles, ...role, ...tenantPrivileges];
      break;
    case SET_GROUPS:
      draft.groups = action.groups
      break;
  }
});

function ProfileProvider({ privilege, children }) {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { i18n } = useTranslation();
  const history = useHistory();
  const { status, error, isSuccess } = useQuery(['profile'], () => fetcher(fetchProfile()), {
    onSuccess: (profile) => {
      if (i18n.language !== profile?.language) {
        i18n.changeLanguage(profile?.language)
        localStorage.setItem('user-language', profile?.language);
      }
      dispatch({ type: SET_PROFILE, profile })
    },
    onError: (error) => {
      console.error(error.message)
    }
  })
  useQuery(['groups'], () => fetcher(fetchMembers({ contractId: '_' })), {
    onSuccess: (groups) => {
      dispatch({ type: SET_GROUPS, groups })
    },
    onError: (error) => {
      console.error(error.message)
    }
  })

  const channel = useChannel(`direct-${state.profile._id}`);

  useEvent(channel, 'direct-notification', (notification = {}) => {
    toast.dark(
      <Growl
        {...notification}
        translated={t(`notifications.${notification.type}`, t(`events.${notification.type}`))}
      />
    , {
      position: 'bottom-right'
    });
  });

  useEvent(channel, 'new-message', (message = {}) => {
    toast.dark(
      <Growl
        {...message}
        translated={t(`notifications.message`)}
      />
    );
  });


  const profileAbility = defineAbilityFor({ ...state.profile, privileges: state.roles });

  return (
    <ProfileAbilityContext.Provider value={profileAbility}>
      <ProfileStateContext.Provider value={{ ...state, status, error, profile: state.profile, isSuccess }}>
        <ProfileDispatchContext.Provider value={dispatch}>
          <PermissionChecker profile={state.profile} privilege={privilege} status={status}>
            {children}
          </PermissionChecker>
        </ProfileDispatchContext.Provider>
      </ProfileStateContext.Provider>
    </ProfileAbilityContext.Provider>
  )
}

function PermissionChecker({ profile = {}, status, privilege, children }) {
    const location = useLocation();
    const history = useHistory();

    const params = useParams();

    const ability = useProfileAbility();

    const hasPermission = privilege ? ability.can(privilege) : true;

    if (status === 'loading' && !profile._id) {

        return (
          <div className="d-flex w-100 h-100 align-items-center justify-content-center">
            <TratoLoader size="xl" />
          </div>
        )
    }

    if (location.pathname.startsWith('/contracts/editor/') && ability.can('VIEW_FRONTDESK') && !hasPermission) {
        return (
            <Redirect
                to={generatePath('/frontdesk/contract/:contractId', {
                  contractId: params.documentId,
                })}
            />
        )
  
    }
    
    if (!hasPermission && status === 'success') {
      const firstMenuOption = menus.find((menu) => ability.can(menu.ability))
      if (firstMenuOption) return <Redirect
        to={generatePath(firstMenuOption.href, params)}
      />
      return (
        <div className="d-flex flex-column justify-content-center align-items-center w-100 h-100">
          <h1>No Perms</h1>
          <Button color="primary" size="lg" onClick={history.goBack}>Return</Button>
        </div>
      )
    }
   
  return children
}

function useProfileState() {
  const context = React.useContext(ProfileStateContext)
  if (context === undefined) {
    throw new Error('useProfileState must be used within a ProfileProvider')
  }
  return context
}

function useProfileDispatch() {
  const context = React.useContext(ProfileDispatchContext)
  if (context === undefined) {
    throw new Error('useProfileDispatch must be used within a ProfileProvider')
  }
  return context
}

function useProfileAbility() {
  const ability = useAbility(ProfileAbilityContext)
  if (ability === undefined) {
    throw new Error('useProfileAbility must be used within a ProfileProvider')
  }
  return ability
}

function useProfile() {
  return [useProfileState(), useProfileDispatch()]
}

export { ProfileProvider, useProfile, useProfileState, useProfileDispatch, useProfileAbility, ProfileCan, ProfileAbilityContext }
